import {
  Facebook,
  Instagram,
  Linkedin,
  Mail,
  MapPin,
  Music2,
  Phone,
} from "lucide-react";
import { useState } from "react";
import { Link } from "react-router-dom";
import Logo from "./Logo";
import { services } from "../data/services";

const exploreLinks = [
  ["/about", "About Us"],
  ["/services", "Services"],
  ["/how-it-works", "How It Works"],
  ["/blog", "Blog"],
  ["/contact", "Contact Us"],
];

export default function Footer() {
  const [newsletterEmail, setNewsletterEmail] = useState("");
  const [newsletterSubmitted, setNewsletterSubmitted] = useState(false);

  function handleNewsletterSubmit(event) {
    event.preventDefault();
    setNewsletterSubmitted(true);
  }

  return (
    <footer className="bg-ink text-white">
      <div className="container grid gap-12 py-16 sm:grid-cols-2 lg:grid-cols-[1.3fr_.8fr_.8fr_1fr]">
        <div>
          <Logo light />
          <p className="mt-6 max-w-xs text-sm leading-7 text-slate-300">
            Your trusted execution partner in Nigeria. Connecting opportunities
            with transparency, accountability and peace of mind.
          </p>
          <div className="mt-6 space-y-3 text-sm text-slate-300">
            <a
              href="tel:+2348000000000"
              className="flex items-center gap-3 transition hover:text-white"
            >
              <Phone size={16} className="shrink-0 text-cyan" />
              +234 800 000 0000
            </a>
            <a
              href="mailto:hello@bridgebyva.com"
              className="flex items-center gap-3 transition hover:text-white"
            >
              <Mail size={16} className="shrink-0 text-cyan" />
              hello@bridgebyva.com
            </a>
          </div>
          <div className="mt-6 flex items-center gap-3">
            {[
              [Facebook, "Facebook", "https://www.facebook.com/bridgebyva"],
              [Instagram, "Instagram", "https://www.instagram.com/bridgebyva"],
              [Linkedin, "LinkedIn", "https://www.linkedin.com/company/bridgebyva"],
              [Music2, "TikTok", "https://www.tiktok.com/@bridgebyva"],
            ].map(([Icon, label, href]) => (
              <a
                key={label}
                href={href}
                target="_blank"
                rel="noreferrer"
                className="grid h-9 w-9 place-items-center rounded-full border border-white/15 text-cyan transition hover:bg-white/10 hover:text-white"
                aria-label={`The Bridge on ${label}`}
              >
                <Icon size={16} />
              </a>
            ))}
          </div>
        </div>
        <div>
          <p className="text-xs font-bold uppercase tracking-[.2em] text-cyan">
            Explore
          </p>
          <div className="mt-5 space-y-3">
            {exploreLinks.map(([to, label]) => (
              <Link
                key={to}
                to={to}
                className="block text-sm text-slate-300 hover:text-white"
              >
                {label}
              </Link>
            ))}
          </div>
        </div>
        <div>
          <p className="text-xs font-bold uppercase tracking-[.2em] text-cyan">
            Services
          </p>
          <div className="mt-5 space-y-3">
            {services.slice(0, 5).map((service) => (
              <Link
                key={service.slug}
                to={`/services/${service.slug}`}
                className="block text-sm text-slate-300 hover:text-white"
              >
                {service.shortTitle}
              </Link>
            ))}
          </div>
        </div>
        <div>
          <p className="text-xs font-bold uppercase tracking-[.2em] text-cyan">
            Keep in touch
          </p>
          <p className="mt-5 text-sm leading-7 text-slate-300">
            Get occasional updates, practical insights and news from The Bridge.
            No spam, just useful information, when it matters.
          </p>
          <form className="mt-5" onSubmit={handleNewsletterSubmit}>
            <label htmlFor="newsletter-email" className="sr-only">
              Email address for updates
            </label>
            <div className="flex rounded-xl border border-white/15 bg-white/[.06] p-1.5 focus-within:border-cyan/70">
              <input
                id="newsletter-email"
                type="email"
                value={newsletterEmail}
                onChange={(event) => {
                  setNewsletterEmail(event.target.value);
                  setNewsletterSubmitted(false);
                }}
                placeholder="Your email address"
                aria-label="Your email address"
                required
                className="min-w-0 flex-1 bg-transparent px-3 text-sm text-white outline-none placeholder:text-slate-400"
              />
              <button
                type="submit"
                className="shrink-0 rounded-lg bg-bridge px-3 py-2 text-xs font-extrabold text-ink transition hover:bg-white focus:outline-none focus:ring-2 focus:ring-cyan/50"
              >
                Subscribe
              </button>
            </div>
            {newsletterSubmitted && (
              <p role="status" className="mt-2 text-xs font-bold text-bridge">
                Thanks, you’re on the list for Bridge updates.
              </p>
            )}
          </form>
          {/* <Link
            to="/pricing"
            className="mt-5 inline-flex items-center gap-2 text-sm font-bold text-bridge hover:text-white"
          >
            Get started <ArrowUpRight size={16} />
          </Link> */}
          <div className="mt-6 flex gap-2 text-xs text-slate-400">
            <MapPin size={15} className="shrink-0 text-cyan" />
            Nigeria · serving clients globally
          </div>
        </div>
      </div>
      <div className="border-t border-white/10">
        <div className="container flex flex-col gap-4 py-5 text-xs text-slate-400 sm:flex-row sm:items-center sm:justify-between">
          <p>
            © {new Date().getFullYear()} The Bridge by VA. All rights reserved.
          </p>
          <div className="flex flex-wrap gap-x-5 gap-y-2">
            <Link to="/legal/privacy" className="hover:text-white">
              Privacy
            </Link>
            <Link to="/legal/terms" className="hover:text-white">
              Terms
            </Link>
            <Link to="/legal/cookies" className="hover:text-white">
              Cookies
            </Link>
            <Link to="/legal/acceptable-use" className="hover:text-white">
              Acceptable use
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
